import { jsxs, jsx } from "react/jsx-runtime";
import React__default from "react";
import { cn } from "../../../lib/utils.js";
import "../shared/quiz-blank.js";
import { KatexText } from "../shared/katex-text.js";
import "react-i18next";
import "../../../node_modules/.pnpm/hls.js@1.6.15/node_modules/hls.js/dist/hls.js";
import "../../../utils/quiz-parser.js";
import "../../../context/DevToolsContext.js";
const printStyles = {
  container: "w-full max-w-[650px]",
  quizSection: "flex flex-col gap-[24px] items-start w-full",
  problemArea: "pl-[40px] w-full",
  problemLine: "w-full",
  problemText: "text-[16px] text-black tracking-[-0.096px] font-normal leading-[48px]",
  titleNumber: "font-dunggeunmiso text-[#ff9f19] text-[24px] font-bold text-center size-[32px] shrink-0 leading-[32px]",
  titleText: "text-[16px] text-black font-bold tracking-[-0.096px] leading-[30px] flex-1",
  inputBoxSm: "bg-white border-[#ccc] border-[1.5px] border-solid rounded-[6px] h-[40px]",
  inputBoxMd: "bg-white border-[#ccc] border-[1.5px] border-solid rounded-[6px] size-[56px]",
  inputBoxLg: "bg-white border-[#ccc] border-[1.5px] border-solid rounded-[6px] h-[40px] w-[88px]"
};
const compactPrintStyles = {
  container: "w-full max-w-[650px]",
  quizSection: "flex flex-col gap-[18px] items-start w-full",
  problemArea: "pl-[40px] w-full",
  problemLine: "w-full",
  problemText: "text-[15px] text-black tracking-[-0.09px] font-normal leading-[26px]",
  titleNumber: "font-dunggeunmiso text-[#fa3] text-[24px] font-bold text-center size-[26px] shrink-0 leading-[normal]",
  titleText: "text-[15px] text-black font-semibold tracking-[-0.09px] leading-[26px] flex-1",
  inputBoxSm: "bg-white border-[#ccc] border-[1.5px] border-solid rounded-[6px] h-[35px]",
  inputBoxMd: "bg-white border-[#ccc] border-[1.5px] border-solid rounded-[6px] size-[40px]",
  inputBoxLg: "bg-white border-[#ccc] border-[1.5px] border-solid rounded-[6px] h-[35px] w-[72px]"
};
function getStyles(compact) {
  return compact ? compactPrintStyles : printStyles;
}
function PrintQuizTitle({ number, children, compact }) {
  const s = getStyles(compact);
  return /* @__PURE__ */ jsxs("div", { className: "flex items-start gap-[8px] w-full", children: [
    /* @__PURE__ */ jsx("span", { className: s.titleNumber, children: number }),
    /* @__PURE__ */ jsx("div", { className: s.titleText, children })
  ] });
}
const DEFAULT_PRINT_SCALE = 0.75;
function PrintQuizImage({ src, alt = "quiz", className, printScale }) {
  const scale = printScale ?? DEFAULT_PRINT_SCALE;
  const [scaledWidth, setScaledWidth] = React__default.useState(null);
  const handleLoad = React__default.useCallback((e) => {
    const naturalWidth = e.currentTarget.naturalWidth;
    setScaledWidth(Math.round(naturalWidth * scale));
  }, [scale]);
  return /* @__PURE__ */ jsx("div", { className: "flex justify-center w-full", children: /* @__PURE__ */ jsx(
    "img",
    {
      src,
      alt,
      className: cn("h-auto", className),
      style: scaledWidth != null ? { width: `${scaledWidth}px` } : void 0,
      onLoad: handleLoad
    }
  ) });
}
function PrintInputBox({ size = "md", children, className, compact }) {
  const s = getStyles(compact);
  const sizeClass = {
    sm: s.inputBoxSm,
    md: s.inputBoxMd,
    lg: s.inputBoxLg
  }[size];
  return /* @__PURE__ */ jsx("div", { className: cn(sizeClass, "flex justify-center items-center text-center", className), children });
}
function PrintWordBank({ items, label = "보기", compact }) {
  if (items.length === 0) return null;
  const s = getStyles(compact);
  return /* @__PURE__ */ jsxs("div", { className: cn("relative bg-[#f2f2f2] rounded-[16px] w-full", compact ? "pt-[38px] pb-[16px] px-[20px]" : "pt-[48px] pb-[24px] px-[24px]"), children: [
    /* @__PURE__ */ jsx("span", { className: cn("absolute top-0 left-[20px] bg-[#999] text-white rounded-bl-[8px] rounded-br-[8px] flex items-center font-bold", compact ? "h-[28px] text-[14px] w-[54px] justify-center" : "h-[28px] px-[12px] text-[14px]"), children: label }),
    /* @__PURE__ */ jsx("div", { className: "flex flex-wrap gap-[10px]", children: items.map((item, idx) => /* @__PURE__ */ jsx(
      "span",
      {
        className: cn(
          s.inputBoxSm,
          "inline-flex items-center justify-center px-[16px] py-[8px] text-black tracking-[-0.09px] text-center",
          compact ? "min-w-[72px] text-[15px]" : "min-w-[80px] text-[16px]"
        ),
        children: /* @__PURE__ */ jsx(KatexText, { text: item.label })
      },
      item.key || `wb-${idx}`
    )) })
  ] });
}
function PrintOrderBoxes({ count, correctOrder, showCorrectAnswers = false, compact }) {
  return /* @__PURE__ */ jsx("div", { className: cn("flex items-center flex-wrap", compact ? "gap-[4px]" : "gap-[8px]"), children: Array.from({ length: count }).map((_, idx) => {
    const orderNum = correctOrder == null ? void 0 : correctOrder.get(idx);
    const showNum = showCorrectAnswers && orderNum != null;
    return /* @__PURE__ */ jsxs("div", { className: cn("flex items-center", compact ? "gap-[4px]" : "gap-[8px]"), children: [
      /* @__PURE__ */ jsx(PrintInputBox, { size: "md", compact, className: "text-[16px] font-semibold text-gray-500", children: showNum ? orderNum : " " }),
      idx < count - 1 && /* @__PURE__ */ jsx("span", { className: "text-[20px] text-[#ccc]", children: "→" })
    ] }, idx);
  }) });
}
function getPrintScale(image, printImage) {
  if ((printImage == null ? void 0 : printImage.printScale) != null) return printImage.printScale;
  if ((image == null ? void 0 : image.printScale) != null) return image.printScale;
  return null;
}
function getImageUrl(image, printImage) {
  if (printImage) {
    const printUrl = printImage.signedUrl || printImage.url;
    if (printUrl) return printUrl;
  }
  if (!image) return null;
  if (image.printSignedUrl) return image.printSignedUrl;
  return image.signedUrl || image.url || null;
}
export {
  PrintInputBox,
  PrintOrderBoxes,
  PrintQuizImage,
  PrintQuizTitle,
  PrintWordBank,
  compactPrintStyles,
  getImageUrl,
  getPrintScale,
  getStyles,
  printStyles
};
//# sourceMappingURL=print-common.js.map
